import {
  EnvConfig,
  Media,
  MediaType,
  useProduct,
  useUpdateMutation
} from '@gripp/shared-logic';
import {
  ImageGalleryImage,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EditCategory from '../../assets/components/editCategory';
import { FilePreviewHeaderActions, FilePreviewPanel } from '../../assets/components/filesFilePreviewPanel';
import { GalleryPanel } from '../../assets/components/filesGalleryPanel';
import { VideoPanel } from '../../assets/components/filesVideoPanel';
import { EditFileForm } from '../../files/components/editFileForm';

const UPDATE_PRODUCT = `
  mutation UpdateProduct($input: MutateProductInput!) {
    updateProduct(input: $input) {
      id
    }
  }
`;

type CategoryItem = { id: string; name: string };

export const ProductFileEdit = () => {
  const { productId = '', mediaId = '' } = useParams();
  const navigate = useNavigate();
  const { product, invalidateQuery } = useProduct(productId);
  const queryClient = useQueryClient();

  const { mutateAsync: updateProduct } = useUpdateMutation({
    modelName: 'product',
    query: UPDATE_PRODUCT,
  });

  const allFiles: Media[] = useMemo(
    () => (product?.media as Media[]) || [],
    [product?.media]
  );

  const selectedMedia = useMemo(
    () => allFiles.find((m) => m.id === mediaId) ?? null,
    [allFiles, mediaId]
  );

  const allFileIds = useMemo(() => allFiles.map((m) => m.id), [allFiles]);

  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>([]);
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);

  useEffect(() => {
    if (selectedMedia?.categories) {
      setSelectedCategories(selectedMedia.categories as CategoryItem[]);
    }
  }, [selectedMedia?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isImage = selectedMedia?.type === MediaType.Image;
  const isVideo = selectedMedia?.type === MediaType.Video;

  const galleryImages: ImageGalleryImage[] = useMemo(() => {
    if (!isImage || !selectedMedia) return [];
    return [
      {
        id: selectedMedia.id,
        url: `${EnvConfig.mediaUrl}/images/${selectedMedia.id}/original.${selectedMedia.extension}`,
        name: selectedMedia.name,
      },
    ];
  }, [isImage, selectedMedia]);

  const backToProduct = () =>
    navigate(`/products/${productId}`);

  const backToPreview = () =>
    navigate(`/products/${productId}/files/${mediaId}`);

  if (!productId || !selectedMedia) return null;

  const previewTitle =
    selectedMedia.name && selectedMedia.name.length > 30
      ? `${selectedMedia.name.substring(0, 27)}...`
      : selectedMedia.name ?? 'File';

  const leftColumn = {
    title: previewTitle,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: backToProduct,
    disableScroll: true,
    headerComponent:
      !isImage && !isVideo ? (
        <FilePreviewHeaderActions media={selectedMedia} />
      ) : undefined,
    mainComponent: isImage ? (
      <GalleryPanel
        images={galleryImages}
        initialIndex={0}
        disableImageChange
      />
    ) : isVideo ? (
      <VideoPanel media={selectedMedia} />
    ) : (
      <FilePreviewPanel media={selectedMedia} />
    ),
  };

  const rightColumn = {
    title: 'topNav.edit',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: backToPreview,
    disableScroll: false,
    mainComponent: (
      <EditFileForm
        fileId={mediaId}
        media={selectedMedia}
        onComplete={backToPreview}
        useCropCircle
        onAfterSave={async () => {
          const mediaList = allFileIds.map((id) => ({ id }));
          await updateProduct({ input: { id: productId, media: mediaList } });
          await invalidateQuery();
        }}
        onDelete={async () => {
          const fileList = allFileIds
            .filter((id) => id !== selectedMedia.id)
            .map((id) => ({ id }));
          await updateProduct({ input: { id: productId, media: fileList } });
          await queryClient.invalidateQueries({ queryKey: ['product'] });
        }}
        onCategoryEdit={() => setIsCategoryPanelOpen((prev) => !prev)}
        externalCategories={selectedCategories}
        onCategoriesChange={setSelectedCategories}
      />
    ),
  };

  const thirdColumn = isCategoryPanelOpen
    ? {
        title: 'file.create.category',
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsCategoryPanelOpen(false),
        mainComponent: (
          <EditCategory
            selectedCategories={selectedCategories}
            onCategoriesChange={setSelectedCategories}
          />
        ),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};
